/**
 * A trava do canário, isolada do axios — pura, e por isso conferível.
 *
 * Por que ela existe: a ApiSistema aponta para PRODUÇÃO e não há base de
 * homologação (o servidor de banco está no limite; a cópia só vem depois da
 * migração). E `sys.foreign_keys` volta vazio para `diagrama`, `projeto`,
 * `componente`, `pinout`, `circuito` e `conector` — nada no banco barra uma
 * escrita errada. A rede de segurança tem que estar do lado que erra.
 *
 * Regra 6 do plano de execução.
 */

import { ErroDeTrava } from "./erros"

const METODOS_DE_ESCRITA = new Set(["post", "put", "patch", "delete"])

/**
 * As únicas rotas de método de escrita que **não mudam dado** e por isso podem
 * passar sem projeto.
 *
 * A lista é curta de propósito e está aqui, e não espalhada em quem chama: uma
 * rota entra nela por decisão consciente, e dá para auditar a decisão inteira
 * lendo cinco linhas.
 */
const ESCRITAS_QUE_NAO_GRAVAM = [
  "api/login",                     // devolve token; não cria nem altera nada
  // Reemite o token com as permissões relidas do banco. É POST por ser uma
  // credencial nova — GET deixaria rastro em log e histórico —, não porque muda
  // dado: nada é escrito. Sem esta linha a trava barra a renovação e a permissão
  // que a pessoa acabou de receber continua sem valer.
  "api/acessos/renovar",
  "api/pinouts/processar-texto",   // só recorta o texto colado para pré-visualizar
  // Relatório. É POST porque leva duas listas no corpo — especialidades e
  // estados —, e não porque muda alguma coisa: a rota só lê contratos.
  "api/relatorios/comercial/especialidade-licenca/por-estado",
]

/**
 * As conferências: rotas de método de escrita que **decidem se gravam por um
 * parâmetro**, e não pelo verbo.
 *
 * São o par `/conferir` e `/aplicar` que a API adotou para toda operação em
 * massa — importação em lote, Excel de fusíveis, regenerar caixa, recalcular
 * descrição. A conferência roda a operação inteira **sem gravar** e devolve o
 * relatório; quem grava é a segunda chamada, com `aplicar=true`.
 *
 * São POST porque levam corpo — planilha, filtros —, e não porque mudam algo.
 * Barrá-las em desenvolvimento tiraria justamente a metade que existe para
 * evitar estrago, e deixaria a tela sem como mostrar o que aconteceria.
 *
 * **A liberação vale só enquanto `aplicar` não for verdadeiro.** Na hora de
 * aplicar, a trava volta a valer inteira — é o mesmo endereço, e é por isso que
 * a decisão olha o parâmetro e não o caminho.
 */
const CONFERENCIAS = [
  // Redesenho do diagrama: roda o motor inteiro e compara com o svg gravado, sem
  // escrever. Quem grava e `desenhar` sem o `/conferir`, e essa fica travada.
  /^api\/diagramas\/\d+\/desenhar\/conferir$/,
  /^api\/projetos\/\d+\/importacao\/conferir$/,
  /^api\/projetos\/\d+\/importacao-de-fusiveis$/,
  /^api\/caixas-fusiveis\/\d+\/regenerar$/,
  /^api\/cobertura\/descricao-recalculada$/,
]

/**
 * Escritas que **gravam, mas não em dado de negócio**: o relato de quem usa o
 * Sistema.
 *
 * O feedback do botão flutuante cria uma linha nova numa tabela só dele e sobe
 * fotos para uma pasta só dele (`feedback/sistema`); marcar "resolvido" mexe
 * numa coluna dessa mesma linha. Nada disso é lido por projeto, catálogo ou
 * pelo site do cliente — o estrago que a trava existe para evitar não tem por
 * onde acontecer. Barrar aqui só deixaria o botão sem funcionar em
 * desenvolvimento, que é justamente onde se acha o problema a relatar.
 *
 * Um relato escrito em desenvolvimento é um relato de verdade: se a API
 * apontar para produção, ele aparece na lista da equipe como qualquer outro.
 */
const ESCRITAS_DE_RELATO = [
  /^api\/feedback-sistema$/,
  /^api\/feedback-sistema\/\d+\/resolvido$/,
]

/** `aplicar=true` em qualquer lugar — query, corpo ou FormData. */
function vaiAplicar(req: RequisicaoParaTrava): boolean {
  const naQuery = String((req.params as Record<string, unknown> | undefined)?.aplicar ?? "")
  if (naQuery.toLowerCase() === "true") return true

  const d = req.data
  if (typeof FormData !== "undefined" && d instanceof FormData) {
    return String(d.get("aplicar") ?? "").toLowerCase() === "true"
  }
  if (d && typeof d === "object") {
    return String((d as Record<string, unknown>).aplicar ?? "").toLowerCase() === "true"
  }
  return false
}

/**
 * O caminho sem a query. A conferência e o aplicar partilham o endereço; quem
 * separa os dois é o parâmetro, lido em `vaiAplicar`.
 */
function caminhoSemQuery(url: string): string {
  const i = url.indexOf("?")
  return i < 0 ? url : url.slice(0, i)
}

/**
 * O prefixo que marca um registro como descartável.
 *
 * Por que existe: o dado global — assunto, marca, veículo, motor, cor de função,
 * usuário — não tem projeto, então o canário 992 não o alcança, e a trava
 * recusava toda escrita global. Resultado: esses caminhos nunca foram
 * exercitados, nem uma vez. Com a API apontando para produção, a escolha era
 * testar no ar ou não testar.
 *
 * O prefixo abre a terceira porta: **criar** registro novo marcado, conferir, e
 * apagar. É seguro porque a busca do site sai da `TabelaPesquisa`, que é gerada
 * a partir de conteúdo — um cadastro sem conteúdo vinculado não aparece para o
 * cliente. A regra que vem junto: nunca vincular conteúdo a um registro ZZTESTE.
 *
 * É a mesma convenção do canário, que se chama `ZZTESTE-FRONT`.
 *
 * **O que isto NÃO protege:** renomear um registro de verdade PARA `ZZTESTE`.
 * A trava vê o que vai ser gravado, não o que está gravado. O acidente que ela
 * evita é o real — abrir a lista de marcas, editar a Fiat sem querer e salvar,
 * que continua barrado porque o corpo diria "Fiat".
 */
export const PREFIXO_DE_TESTE = "ZZTESTE"

/**
 * O campo que dá nome ao registro, na ordem em que os cadastros o usam. O
 * primeiro presente decide — se ele não começa pelo prefixo, não é registro de
 * teste, e a escrita global não passa.
 */
const CAMPOS_DE_NOME = [
  "nome", "titulo", "descricao",
  "marcaNome", "veiculoModelo", "sistemaNome", "assuntoNome", "motorNome",
  // O cadastro de veículo manda o nome em `modelo`. Sem esta linha nem o veículo
  // ZZTESTE nascia em desenvolvimento — e sem ele, nada do veículo se testava.
  "modelo",
]

function nomeDoRegistro(d: unknown): string | null {
  if (typeof FormData !== "undefined" && d instanceof FormData) {
    for (const campo of CAMPOS_DE_NOME) {
      const v = d.get(campo)
      if (typeof v === "string" && v !== "") return v
    }
    return null
  }
  if (!d || typeof d !== "object") return null
  const c = d as Record<string, unknown>
  for (const campo of CAMPOS_DE_NOME) {
    const v = c[campo]
    if (typeof v === "string" && v !== "") return v
  }
  return null
}

function ehDeTeste(texto: string | null | undefined): boolean {
  return (texto ?? "").trimStart().toUpperCase().startsWith(PREFIXO_DE_TESTE)
}

export type RequisicaoParaTrava = {
  method?: string
  url?: string
  params?: unknown
  data?: unknown
  semProjeto?: boolean
  /**
   * O nome do registro que esta escrita atinge, declarado por quem chama.
   *
   * Existe pelo mesmo motivo que `projetoDeclarado`: o `DELETE api/marcas/{id}`
   * não tem corpo, então a trava não tem como ler o nome. A tela tem o registro
   * na mão e sabe o que está apagando; então diz.
   */
  alvoDeTeste?: string
  /**
   * Projeto declarado pelo chamador. Existe porque nem toda escrita carrega o
   * projeto no corpo ou na query: `DELETE api/componentes/{id}` não carrega
   * nada, e a trava barraria por precaução — corretamente, mas deixando a
   * exclusão sem funcionar nem no canário. Quem chama sabe de qual projeto está
   * apagando; então diz.
   */
  projetoDeclarado?: number
}

/**
 * Descobre quais projetos a requisição toca. Procura, nesta ordem:
 * `projetoId` na query, `projetoId` na URL, `projetoId` no corpo, e o `id` de
 * cada item de `pinouts` — que é onde o `PinoutUpsertItem` carrega o projeto
 * (sim, o campo se chama `id`; o pinout é `pinoutId`).
 *
 * `null` = não achou nenhum. A trava barra por precaução: quem sabe que a rota
 * não é de projeto marca com `semProjeto`.
 */
export function projetosAlvo(req: RequisicaoParaTrava): number[] | null {
  const achados = new Set<number>()

  if (req.projetoDeclarado != null) achados.add(req.projetoDeclarado)

  const params = req.params as Record<string, unknown> | undefined
  const daQuery = params?.["projetoId"]
  if (daQuery != null && daQuery !== "") achados.add(Number(daQuery))

  const naUrl = /[?&]projetoId=(\d+)/.exec(req.url ?? "")
  if (naUrl?.[1]) achados.add(Number(naUrl[1]))

  const corpo = req.data
  if (corpo && typeof corpo === "object") {
    const c = corpo as Record<string, unknown>
    if (typeof c["projetoId"] === "number") achados.add(c["projetoId"])

    const pinouts = c["pinouts"]
    if (Array.isArray(pinouts)) {
      for (const item of pinouts) {
        const id = (item as Record<string, unknown> | null)?.["id"]
        if (typeof id === "number") achados.add(id)
      }
    }
  }

  return achados.size > 0 ? [...achados] : null
}

/**
 * Levanta `ErroDeTrava` se a requisição escreveria fora do projeto canário.
 * `canario` nulo = trava desligada (é o build de produção).
 */
export function conferirTrava(req: RequisicaoParaTrava, canario: number | null): void {
  if (canario === null) return
  if (!METODOS_DE_ESCRITA.has((req.method ?? "get").toLowerCase())) return

  const onde = `${(req.method ?? "get").toUpperCase()} ${req.url ?? ""}`
  const url = req.url ?? ""

  if (ESCRITAS_QUE_NAO_GRAVAM.some((r) => url.startsWith(r))) return

  // A conferência não grava — e é ela que mostra o estrago antes de haver
  // estrago. Passa; o `aplicar` correspondente não passa.
  const caminho = caminhoSemQuery(url)
  const naQuery = new URLSearchParams(url.slice(url.indexOf("?") + 1))
  const aplicaNaUrl = url.includes("?") && naQuery.get("aplicar")?.toLowerCase() === "true"

  if (CONFERENCIAS.some((r) => r.test(caminho)) && !aplicaNaUrl && !vaiAplicar(req)) return

  if (ESCRITAS_DE_RELATO.some((r) => r.test(caminho))) return

  /**
   * **`semProjeto` não libera escrita.**
   *
   * Ele já liberou, e estava errado: a marca nasceu para o login — um POST que
   * não grava nada — e virou porta para qualquer escrita sem projeto. Só que
   * escrita sem projeto é justamente a mais perigosa: catálogo global, cor de
   * função, projeto de correias. Renomear um sistema em desenvolvimento
   * renomearia em **todos os projetos de produção**, ao vivo.
   *
   * E não há canário para esse dado: o projeto 992 protege o módulo de pinout
   * porque lá existe um projeto de brinquedo. Para o que é global não existe
   * equivalente, e a única resposta honesta em desenvolvimento é recusar.
   */
  if (req.semProjeto) {
    // A porta do registro descartável: quem chama declarou o alvo, ou o corpo
    // traz o nome. Nos dois casos o prefixo é o que autoriza.
    if (ehDeTeste(req.alvoDeTeste) || ehDeTeste(nomeDoRegistro(req.data))) return

    const nome = req.alvoDeTeste ?? nomeDoRegistro(req.data)
    throw new ErroDeTrava(
      `TRAVA DO CANÁRIO: ${onde} grava dado GLOBAL — que vale para todos os projetos — e não ` +
        `existe canário para dado global. Em desenvolvimento a API aponta para PRODUÇÃO, então ` +
        `esta escrita foi bloqueada antes de sair. No ar, onde a trava não existe, ela funciona.` +
        (nome
          ? ` O registro é "${nome}", que não começa por ${PREFIXO_DE_TESTE} — só registro ` +
            `marcado assim pode ser criado e apagado em desenvolvimento.`
          : ` Para exercitar este caminho, crie um registro novo começando por ` +
            `${PREFIXO_DE_TESTE}; para apagá-lo, declare o nome com deTeste(nome).`),
    )
  }

  const alvos = projetosAlvo(req)

  if (alvos === null) {
    throw new ErroDeTrava(
      `TRAVA DO CANÁRIO: ${onde} não diz em que projeto escreve. Passe projetoId, ou declare o ` +
        `alvo com noProjeto(id) quando ele só estiver no caminho da URL.`,
    )
  }

  const invasores = alvos.filter((p) => p !== canario)
  if (invasores.length > 0) {
    throw new ErroDeTrava(
      `TRAVA DO CANÁRIO: ${onde} escreveria no(s) projeto(s) ${invasores.join(", ")}, mas em ` +
        `desenvolvimento só o canário ${canario} é permitido. A API aponta para PRODUÇÃO — esta ` +
        `escrita foi bloqueada antes de sair.`,
    )
  }
}
